import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { PageHeader, Modal, Badge, Avatar, EmptyState, useToast, Confirm } from '../ui';
import { useLang } from '../Layout';
import { FaPlus, FaTrash, FaUserCheck, FaPhone, FaChevronRight } from 'react-icons/fa6';

const TRADES = ['plumber', 'electrician', 'rajmistri', 'road mistri', 'tis mistri', 'welder', 'painter', 'other'];
const tradeLabel = { plumber: 'প্লাম্বার', electrician: 'ইলেকট্রিশিয়ান', rajmistri: 'রাজমিস্ত্রি', 'road mistri': 'রোড মিস্ত্রি', 'tis mistri': 'টিস মিস্ত্রি', welder: 'ওয়েল্ডার', painter: 'পেইন্টার', other: 'অন্যান্য' };

const BILLINGS = [
  { key: 'daily', bn: 'দৈনিক ভিত্তিক', en: 'Daily wage', hintBn: 'কর্মীরা প্রতিদিন মজুরি পায় — হাজিরা অনুযায়ী', hintEn: 'Workers get paid daily per attendance' },
  { key: 'contract', bn: 'কন্ট্রাক্ট/চুক্তি ভিত্তিক', en: 'Contract based', hintBn: 'কাজ অনুযায়ী সাপ্তাহিক বিল পাবে (মাল-বিতরণের কাজের বিবরণ দিয়ে হিসাব)', hintEn: 'Paid weekly by the work done (from stock-out work descriptions)' },
];

const T = {
  bn: {
    title: 'কন্ট্রাক্টর', sub: 'জন কন্ট্রাক্টর', new: 'নতুন কন্ট্রাক্টর', name: 'নাম', trade: 'কাজের ধরন',
    phone: 'ফোন', active: 'সক্রিয়', inactive: 'বন্ধ', save: 'সংরক্ষণ করুন',
    workers: 'জন কর্মী', createLogin: 'ফোন দিয়ে লগইন অ্যাকাউন্ট বানান', password: 'পাসওয়ার্ড',
    empty: 'কোনো কন্ট্রাক্টর নেই। নতুন যোগ করুন।', delTitle: 'কন্ট্রাক্টর মুছুন?',
    delMsg: 'কন্ট্রাক্টর ও তার সব কর্মী মুছে যাবে।', added: 'কন্ট্রাক্টর যোগ হয়েছে',
    billingType: 'কীভাবে টাকা পাবে?', detail: 'বিস্তারিত দেখুন',
  },
  en: {
    title: 'Contractors', sub: 'contractors', new: 'New Contractor', name: 'Name', trade: 'Trade',
    phone: 'Phone', active: 'Active', inactive: 'Inactive', save: 'Save',
    workers: 'workers', createLogin: 'Create phone login account', password: 'Password',
    empty: 'No contractors yet. Add your first one.', delTitle: 'Delete Contractor?',
    delMsg: 'This contractor and all their workers will be deleted.', added: 'Contractor added',
    billingType: 'How does this contractor get paid?', detail: 'View details',
  },
};

export default function Contractors({ role }) {
  const { lang } = useLang(); const t = T[lang];
  const navigate = useNavigate();
  const isOwner = role === 'owner';
  const isManager = role === 'manager';
  const base = isManager ? '/manager' : '/owner';
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({ name: '', trade: 'plumber', phone: '', billing: 'daily', create_login: false, password: '123456' });
  const [err, setErr] = useState('');

  const load = () => api.get(`${base}/contractors`).then(({ data }) => setList(data.contractors)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault(); setErr('');
    try {
      await api.post(`${base}/contractors`, form);
      toast(t.added, 'success');
      setShow(false); setForm({ name: '', trade: 'plumber', phone: '', billing: 'daily', create_login: false, password: '123456' }); load();
    } catch (e) { setErr(e.response?.data?.message || 'সমস্যা হয়েছে'); }
  }
  async function del(id) { await api.delete(`${base}/contractors/${id}`); load(); }

  return (
    <div className="space-y-4">
      <PageHeader title={t.title} subtitle={`${list.length} ${t.sub}`}
        actions={<button className="btn-primary text-sm" onClick={() => { setShow(true); setErr(''); }}><FaPlus /> {t.new}</button>} />

      <div className="grid sm:grid-cols-2 gap-3">
        {list.map((ct) => {
          const isContract = ct.billing === 'contract';
          return (
          <div key={ct.id} className="bg-white rounded-xl border border-ink-100 shadow-soft p-4 cursor-pointer hover:border-brand-300 hover:shadow-pop transition" onClick={() => navigate(`/contractors/${ct.id}`)}>
            <div className="flex items-center gap-3">
              <Avatar name={ct.name} className="w-11 h-11 text-sm" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-ink-900 truncate">{ct.name}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-xs text-ink-400">{tradeLabel[ct.trade] || ct.trade}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isContract ? 'bg-violet-50 text-violet-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {isContract ? (lang === 'bn' ? 'কন্ট্রাক্ট' : 'Contract') : (lang === 'bn' ? 'দৈনিক' : 'Daily')}
                  </span>
                </div>
              </div>
              <Badge color={ct.is_active ? 'active' : 'paused'}>{ct.is_active ? t.active : t.inactive}</Badge>
              <FaChevronRight className="text-ink-300 shrink-0" />
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-ink-50 text-xs text-ink-500">
              <span className="flex items-center gap-1.5"><FaUserCheck className="text-brand-500" /> {ct.worker_count} {t.workers}</span>
              <span className="flex items-center gap-1.5">{ct.phone ? <><FaPhone className="text-ink-300" /> {ct.phone}</> : '—'}</span>
              {isOwner && (
                <button onClick={(e) => { e.stopPropagation(); setConfirmDel(ct); }} className="p-1.5 text-ink-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"><FaTrash size={12} /></button>
              )}
            </div>
          </div>
          );
        })}
      </div>
      {list.length === 0 && <div className="bg-white rounded-xl border border-ink-100 shadow-soft"><EmptyState icon="👷" title={t.empty} /></div>}

      <Modal open={show} onClose={() => setShow(false)} title={t.new}>
        <form onSubmit={save} className="space-y-3.5">
          {err && <div className="text-sm bg-rose-50 text-rose-600 px-3 py-2 rounded-xl">⚠️ {err}</div>}
          <div><label className="label">{t.name} <span className="text-rose-500">*</span></label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><label className="label">{t.trade} <span className="text-rose-500">*</span></label>
            <select className="input" value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })}>{TRADES.map((tr) => <option key={tr} value={tr}>{tradeLabel[tr] || tr}</option>)}</select></div>
          <div>
            <label className="label">{t.billingType}</label>
            <div className="space-y-2">
              {BILLINGS.map((b) => (
                <label key={b.key} className={`flex items-start gap-2.5 border rounded-xl px-3.5 py-3 cursor-pointer transition ${form.billing === b.key ? 'border-brand-400 bg-brand-50/50' : 'border-ink-100 bg-white'}`}>
                  <input type="radio" name="billing" checked={form.billing === b.key} onChange={() => setForm({ ...form, billing: b.key })} className="accent-brand-500 mt-0.5" />
                  <span>
                    <span className="block text-sm font-semibold text-ink-800">{b[lang === 'bn' ? 'bn' : 'en']}</span>
                    <span className="block text-xs text-ink-400 mt-0.5">{b[lang === 'bn' ? 'hintBn' : 'hintEn']}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div><label className="label">{t.phone}</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} inputMode="tel" /></div>
          <label className="flex items-center gap-2.5 text-sm text-ink-700 bg-ink-50 border border-ink-100 rounded-xl px-3.5 py-3 cursor-pointer">
            <input type="checkbox" checked={form.create_login} onChange={(e) => setForm({ ...form, create_login: e.target.checked })} className="accent-brand-500 w-4 h-4" />
            <FaUserCheck className="text-brand-500" /> {t.createLogin}
          </label>
          {form.create_login && <div><label className="label">{t.password}</label><input className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>}
          <button className="btn-primary w-full py-3">{t.save}</button>
        </form>
      </Modal>

      {confirmDel && (
        <Confirm
          open
          onClose={() => setConfirmDel(null)}
          onConfirm={() => del(confirmDel.id)}
          title={t.delTitle}
          message={`"${confirmDel.name}" ${t.delMsg}`} />
      )}
    </div>
  );
}